import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
	getCountries,
	getFieldList,
	getMultiTexts,
	getPropertyInfo,
	getReservation,
	getReservationProfiles,
	getSettingList,
	getToken,
	adyenWebhook,
	updateReservationProfile,
} from '../api';

import type { RootState } from './../store';

const initialState = {
	loading: true,
	toast: false,
	token: null,
	confirmationNo: null,
	countries: null,
	fields: null,
	settings: null,
	reservation: null,
	propertyInfo: null,
	emailPrimary: null,
	phonePrimary: null,

	error: {
		fetchToken: null,
		fetchCountries: null,
		fetchFields: null,
		fetchSettingLists: null,
		fetchReservation: null,
		fetchPropertyInfo: null,
		fetchReservationProfiles: null,
		updateReservation: null,
		fetchTexts: null,
	},
	profiles: null,
	isPostComplete: null,
	multiTexts: null,
} as any;

export const fetchToken = createAsyncThunk(
	'Profile/FetchToken',
	async (propertyCode: any) => {
		const response = await getToken(propertyCode);
		return response?.data?.token;
	}
);

export const fetchPropertyInfo = createAsyncThunk(
	'Profile/FetchPropertyInfo',
	async (token: string, thunkAPI) => {
		try {
			const response = await getPropertyInfo(token);
			if (response && response.data && response.data.data) {
				return response.data.data;
			} else {
				// If the response or its data property is not as expected, throw an error
				throw new Error('Invalid response structure');
			}
		} catch (error) {
			console.error('Error fetching property info:', error);
			throw error; // Re-throw the error to propagate it
		}
	}
);

export const fetchCountries = createAsyncThunk(
	'Profile/FetchCountries',
	async (token: string, thunkAPI) => {
		const response = await getCountries(token);
		return response?.data?.data;
	}
);

export const fetchReservationProfiles = createAsyncThunk<
	any,
	{
		token: string;
		confirmationNo: string;
	}
>(
	'Profile/FetchReservationProfiles',
	async (reservationProfileParam, thunkAPI) => {
		const response = await getReservationProfiles(
			reservationProfileParam?.token,
			reservationProfileParam?.confirmationNo
		);
		return response?.data?.data;
	}
);

export const fetchFields = createAsyncThunk(
	'Profile/FetchFields',
	async (token: string, thunkAPI) => {
		const response = await getFieldList(token);
		return response?.data?.data;
	}
);

export const fetchSettingLists = createAsyncThunk(
	'Profile/SettingFields',
	async (token: string, thunkAPI) => {
		const response = await getSettingList(token);
		return response?.data?.data;
	}
);

export const fetchReservation = createAsyncThunk<
	{
		addressLine1: string;
		adultCount: number;
		arrivalDate: string;
		childCount: number;
		city: string;
		createdDate: string;
		departureDate: string;
		firstName: string;
		gender: string;
		isPreCheckInDone: boolean;
		isPrimary: boolean;
		lastName: string;
		pincode: string;
		pmsConfirmationNo: string;
		rateCode: string;
		rateCodeAmount: number;
		reservationID: number;
		reservationNameID: number;
		roomTypeName: string;
		propertyName: string;
		roomNo: string;
		state: string;
		title: string;
		balanceAmount: string;
		totalAmount: string;
		printRate?: boolean;
		error?: string;
	},
	{ token: string; confirmationNo: string; propertyCode: string },
	{
		rejectValue: any;
	}
>('Profile/FetchReservation', async (reservationParam, _thunkAPI) => {
	const { token, confirmationNo, propertyCode } = reservationParam;
	if (
		propertyCode !== '' &&
		propertyCode !== null &&
		propertyCode !== undefined
	) {
		const response = await getReservation(token, confirmationNo, propertyCode);
		// if (response?.data?.data !== null && response?.data?.data !== undefined) { DUEIN -- status
		// 	console.log('reservation fetch');
		// 	const arrivalDate = response?.data?.data?.arrivalDate;

		// 	const today = new Date();
		// 	today.setHours(0, 0, 0, 0);
		// 	if (arrivalDate && new Date(arrivalDate) < today) {
		// 		console.log('arrivalDate is less than today');
		// 		return _thunkAPI.rejectWithValue({
		// 			message: 'Reservation has Expired.',
		// 			code: '301',
		// 			response: null,
		// 		});
		// 	}
		// }
		if (
			response?.data?.data !== null &&
			response?.data?.data !== undefined &&
			response?.data?.isPrecheckInAllowed === false
		) {
			return _thunkAPI.rejectWithValue({
				message: 'Reservation has Expired.',
				code: '301',
				response: null,
			});
		}
		if (response?.data?.data === null || response?.data?.result === false) {
			return _thunkAPI.rejectWithValue({
				message: 'Reservation not found.',
				code: '105',
				response: null,
			});
		}
		return response?.data?.data;
	}
});

export const callAdyenWebhook = createAsyncThunk<
	any,
	{ token: string; orderRef: string; redirectResult: string }
>('Profile/AdyenWebhook', async (model, _thunkAPI) => {
	const { token, orderRef, redirectResult } = model;
	const response = await adyenWebhook(token, orderRef, redirectResult);
	if (response?.data?.result !== true) {
		return _thunkAPI.rejectWithValue({
			message: response?.data?.message ?? 'Payment failed',
			code: '301',
			response: null,
		});
	}
	return response?.data;
});

export const updateReservation = createAsyncThunk<
	{
		profiles: object;
		reservation: {
			addressLine1: string;
			adultCount: number;
			arrivalDate: string;
			childCount: number;
			city: string;
			createdDate: string;
			departureDate: string;
			firstName: string;
			gender: string;
			isPreCheckInDone: boolean;
			isPrimary: boolean;
			lastName: string;
			pincode: string;
			pmsConfirmationNo: string;
			rateCode: string;
			rateCodeAmount: number;
			reservationID: number;
			reservationNameID: number;
			roomTypeName: string;
			propertyName: string;
			roomNo: string;
			state: string;
			title: string;
			printRate: string;
		};
	},
	{
		token: string;
		reservationID: number;
		profileID: number;
		pmsConfirmationNo: string;
		title: string;
		firstName: string;
		middleName: string;
		lastName: string;
		email: string;
		phoneNo_Res: string;
	}
>('Profile/UpdateReservation', async (reservationParam, _thunkAPI) => {
	await updateReservationProfile(reservationParam);
	const profilesResponse = await getReservationProfiles(
		reservationParam?.token,
		reservationParam?.pmsConfirmationNo
	);
	const reservationResponse = await getReservation(
		reservationParam?.token,
		reservationParam?.pmsConfirmationNo,
		''
	);
	return {
		profiles: profilesResponse?.data?.data,
		reservation: reservationResponse?.data?.data,
	};
});

export const fetchTexts = createAsyncThunk(
	'Master/FetchMultiTexts',
	async (payload: { token: string; language: string }, thunkAPI) => {
		const { token, language } = payload;
		const response = await getMultiTexts(token, language);
		return response?.data?.data;
	}
);

const sessionSlice = createSlice({
	name: 'session',
	initialState,
	reducers: {
		display: (state, action) => {
			state.loading = action?.payload;
		},
		displayToast: (state, action) => {
			state.toast = action?.payload;
		},
		clearError: (state) => {
			state.error = {
				fetchToken: null,
				fetchCountries: null,
				fetchFields: null,
				fetchSettingLists: null,
				fetchReservation: null,
				fetchPropertyInfo: null,
				fetchReservationProfiles: null,
				updateReservation: null,
				fetchTexts: null,
			};
		},
		resetPostComplete: (state) => {
			state.isPostComplete = false;
		},
		setEmail: (state, action) => {
			state.emailPrimary = action.payload;
		},
		setPhone: (state, action) => {
			state.phonePrimary = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchToken.fulfilled, (state, action) => {
			state.token = action?.payload;
			state.error.fetchToken = null;
		});

		builder.addCase(fetchToken.rejected, (state, action) => {
			state.error.fetchToken = action?.error;
		});

		builder.addCase(fetchPropertyInfo.fulfilled, (state, action) => {
			state.propertyInfo = action?.payload;
			state.error.fetchPropertyInfo = null;
			//state.loading = false;
		});

		builder.addCase(fetchPropertyInfo.rejected, (state, action) => {
			state.error.fetchPropertyInfo = action?.error;
			state.loading = false;
		});

		builder.addCase(fetchCountries.fulfilled, (state, action) => {
			state.countries = action?.payload;
			state.error.fetchCountries = null;
		});

		builder.addCase(fetchCountries.rejected, (state, action) => {
			state.error.fetchCountries = action?.error;
		});

		builder.addCase(fetchFields.fulfilled, (state, action) => {
			state.fields = action?.payload;
			state.error.fetchFields = null;
		});

		builder.addCase(fetchFields.rejected, (state, action) => {
			state.error.fetchFields = action?.error;
		});

		builder.addCase(fetchSettingLists.fulfilled, (state, action) => {
			state.settings = action?.payload;
			state.error.fetchSettingLists = null;
		});

		builder.addCase(fetchSettingLists.rejected, (state, action) => {
			state.error.fetchSettingLists = action?.error;
		});

		builder.addCase(fetchReservation.fulfilled, (state, action) => {
			state.reservation = {
				AddressLine1: action?.payload?.addressLine1,
				AdultCount: action?.payload?.adultCount,
				ArrivalDate: action?.payload?.arrivalDate,
				ChildCount: action?.payload?.childCount,
				City: action?.payload?.city,
				CreatedDate: action?.payload?.createdDate,
				DepartureDate: action?.payload?.departureDate,
				FirstName: action?.payload?.firstName,
				Gender: action?.payload?.gender,
				IsPreCheckInDone: action?.payload?.isPreCheckInDone,
				IsPrimary: action?.payload?.isPrimary,
				LastName: action?.payload?.lastName,
				Pincode: action?.payload?.pincode,
				PmsConfirmationNo: action?.payload?.pmsConfirmationNo,
				RateCode: action?.payload?.rateCode,
				RateCodeAmount: action?.payload?.rateCodeAmount,
				ReservationID: action?.payload?.reservationID,
				ReservationNameID: action?.payload?.reservationNameID,
				RoomTypeName: action?.payload?.roomTypeName,
				PropertyName: action?.payload?.propertyName,
				RoomNo: action?.payload?.roomNo,
				State: action?.payload?.state,
				Title: action?.payload?.title,
				BalanceAmount: action?.payload?.balanceAmount,
				TotalAmount: action?.payload?.totalAmount,
				PrintRate: action?.payload?.printRate,
			};
			state.error.fetchReservation = null;
			console.log(
				'Succeessss Msg--' + JSON.stringify(action?.payload?.printRate)
			);
		});

		builder.addCase(fetchReservation.rejected, (state, action) => {
			console.log('Error Msg--' + JSON.stringify(action?.error));
			state.loading = false;
			state.error.fetchReservation = action?.payload ?? action?.error;
		});

		builder.addCase(updateReservation.fulfilled, (state, action) => {
			state.error.updateReservation = null;
			state.isPostComplete = true;
			state.profiles = action?.payload?.profiles;
			state.reservation = {
				AddressLine1: action?.payload?.reservation?.addressLine1,
				AdultCount: action?.payload?.reservation?.adultCount,
				ArrivalDate: action?.payload?.reservation?.arrivalDate,
				ChildCount: action?.payload?.reservation?.childCount,
				City: action?.payload?.reservation?.city,
				CreatedDate: action?.payload?.reservation?.createdDate,
				DepartureDate: action?.payload?.reservation?.departureDate,
				FirstName: action?.payload?.reservation?.firstName,
				Gender: action?.payload?.reservation?.gender,
				IsPreCheckInDone: action?.payload?.reservation?.isPreCheckInDone,
				IsPrimary: action?.payload?.reservation?.isPrimary,
				LastName: action?.payload?.reservation?.lastName,
				Pincode: action?.payload?.reservation?.pincode,
				PmsConfirmationNo: action?.payload?.reservation?.pmsConfirmationNo,
				RateCode: action?.payload?.reservation?.rateCode,
				RateCodeAmount: action?.payload?.reservation?.rateCodeAmount,
				ReservationID: action?.payload?.reservation?.reservationID,
				ReservationNameID: action?.payload?.reservation?.reservationNameID,
				RoomTypeName: action?.payload?.reservation?.roomTypeName,
				PropertyName: action?.payload?.reservation?.propertyName,
				RoomNo: action?.payload?.reservation?.roomNo,
				State: action?.payload?.reservation?.state,
				Title: action?.payload?.reservation?.title,
				PrintRate: action?.payload?.reservation?.printRate,
			};
			console.log(
				'Succeessss Msg--' + JSON.stringify(action?.payload?.reservation)
			);
		});

		builder.addCase(updateReservation.rejected, (state, action) => {
			state.error.updateReservation = action?.error;
		});

		builder.addCase(fetchReservationProfiles.fulfilled, (state, action) => {
			state.profiles = action?.payload;
			state.error.fetchReservationProfiles = null;
		});

		builder.addCase(fetchReservationProfiles.rejected, (state, action) => {
			state.error.fetchReservationProfiles = action?.error;
		});

		builder.addCase(fetchTexts.fulfilled, (state, action) => {
			state.multiTexts = action?.payload;
			state.error.fetchTexts = null;
		});
		builder.addCase(fetchTexts.rejected, (state, action) => {
			state.error.fetchTexts = action?.error;
		});
		builder.addCase(callAdyenWebhook.rejected, (state, action) => {
			console.log('Error Msg--' + JSON.stringify(action?.error));
			state.loading = false;
			state.error.callAdyenWebhook = action?.payload ?? action?.error;
		});
		builder.addCase(callAdyenWebhook.fulfilled, (state, action) => {
			state.error.callAdyenWebhook = null;
			state.isPostComplete = true;
		});
	},
});

// export const {  } = sessionSlice.actions;
export const {
	display,
	clearError,
	resetPostComplete,
	displayToast,
	setEmail,
	setPhone,
} = sessionSlice.actions;
export const selectSession = (state: RootState) => state.session;
//console.log('session-==>', JSON.stringify(selectSession));
export default sessionSlice.reducer;
